/**
 * Indispensability Engine
 * 
 * Makes Decision Sales OS indispensable by:
 * - Showing real-time revenue impact
 * - Daily execution score
 * - What you're missing by not acting
 * - Progress tracking
 */

import { useMemo } from 'react';
import { Lead } from '@/hooks/useLeads';
import { Deal } from '@/hooks/useDeals';
import { formatCurrency } from '@/hooks/useUnifiedMetrics';
import { TrendingDown, AlertCircle, Target, Zap } from 'lucide-react';
import { differenceInHours, differenceInDays } from 'date-fns';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';

interface IndispensabilityEngineProps {
  leads: Lead[];
  deals: Deal[];
}

export function IndispensabilityEngine({ leads, deals }: IndispensabilityEngineProps) {
  const metrics = useMemo(() => {
    const now = new Date();
    let revenueAtRisk = 0;
    let missedOpportunities = 0;
    let executionScore = 100;
    const criticalActions: Array<{ name: string; value: number; reason: string }> = [];

    // Calculate revenue at risk from overdue actions and SLA violations
    leads.forEach(lead => {
      if (lead.pipeline_stage === 'won' || lead.pipeline_stage === 'lost') return;

      const deal = deals.find(d => d.lead_id === lead.id && d.stage !== 'won' && d.stage !== 'lost');
      const dealValue = deal?.value || 0;
      if (dealValue === 0) return; // Only calculate risk for leads with deals

      let leadRisk = 0;
      let isCritical = false;

      // Check overdue follow-ups
      const nextFollowUp = (lead as any).next_follow_up_at ? new Date((lead as any).next_follow_up_at) : null;
      if (nextFollowUp && nextFollowUp < now) {
        const hoursOverdue = differenceInHours(now, nextFollowUp);
        const daysOverdue = Math.max(0, Math.floor(hoursOverdue / 24));
        
        // Revenue decay: 2% per day overdue (more conservative)
        const decay = Math.min(daysOverdue * 0.02, 0.3); // Max 30% decay
        leadRisk += dealValue * decay;
        isCritical = daysOverdue > 0;

        // Penalty for execution score
        executionScore -= Math.min(daysOverdue * 5, 30);
      }

      // Check SLA violations
      const lastContact = lead.last_interaction_at ? new Date(lead.last_interaction_at) : null;
      if (!lastContact) {
        // Never contacted - high risk (especially for hot/warm)
        if (lead.temperature === 'hot') {
          leadRisk += dealValue * 0.20; // 20% risk for hot never contacted
        } else if (lead.temperature === 'warm') {
          leadRisk += dealValue * 0.10; // 10% risk for warm never contacted
        } else {
          leadRisk += dealValue * 0.05; // 5% risk for cold never contacted
        }
        missedOpportunities++;
        executionScore -= 10;
        isCritical = true;
      } else {
        const hoursSinceContact = differenceInHours(now, lastContact);
        const daysSinceContact = Math.floor(hoursSinceContact / 24);

        // SLA: Hot = 6h, Warm = 24h, Cold = 72h
        const slaHours = lead.temperature === 'hot' ? 6 : lead.temperature === 'warm' ? 24 : 72;
        
        if (hoursSinceContact > slaHours) {
          const slaViolationDays = Math.max(0, Math.floor((hoursSinceContact - slaHours) / 24));
          // Risk increases with violation days, but more conservatively
          const riskMultiplier = lead.temperature === 'hot' ? 0.05 : lead.temperature === 'warm' ? 0.03 : 0.01;
          leadRisk += dealValue * (riskMultiplier * Math.min(slaViolationDays, 7)); // Cap at 7 days
          executionScore -= Math.min(slaViolationDays * 3, 20);
          isCritical = slaViolationDays > 0;
        }
      }

      revenueAtRisk += leadRisk;

      // Add to critical actions if significant risk
      if (isCritical && dealValue > 0 && leadRisk > 0) {
        const daysOverdue = nextFollowUp && nextFollowUp < now 
          ? Math.floor(differenceInHours(now, nextFollowUp) / 24)
          : 0;
        const lastContact = lead.last_interaction_at ? new Date(lead.last_interaction_at) : null;
        const daysSinceContact = lastContact ? Math.floor(differenceInHours(now, lastContact) / 24) : null;
        
        let reason = '';
        if (daysOverdue > 0) {
          reason = `${daysOverdue} day${daysOverdue > 1 ? 's' : ''} overdue`;
        } else if (!lastContact) {
          reason = 'Never contacted';
        } else if (daysSinceContact !== null) {
          reason = `${daysSinceContact} day${daysSinceContact > 1 ? 's' : ''} since contact`;
        }

        if (reason) {
          criticalActions.push({
            name: lead.name,
            value: dealValue,
            reason,
          });
        }
      }
    });

    // Calculate what you're missing
    const totalPipelineValue = deals
      .filter(d => d.stage !== 'won' && d.stage !== 'lost' && d.value)
      .reduce((sum, d) => sum + (d.value || 0), 0);

    const missedRevenue = revenueAtRisk;
    const potentialLoss = missedRevenue + (totalPipelineValue * 0.1); // 10% of pipeline at risk

    return {
      revenueAtRisk: Math.round(revenueAtRisk),
      missedOpportunities,
      executionScore: Math.max(0, Math.round(executionScore)),
      criticalActions: criticalActions.slice(0, 3),
      totalPipelineValue,
      potentialLoss: Math.round(potentialLoss),
    };
  }, [leads, deals]);

  if (metrics.revenueAtRisk === 0 && metrics.missedOpportunities === 0) {
    return null;
  }

  return (
    <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4">
      <div className="flex items-start gap-3">
        <div className="rounded-full bg-amber-500/20 p-2">
          <AlertCircle className="w-5 h-5 text-amber-500" />
        </div>
        
        <div className="flex-1 space-y-3">
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-1">
              Revenue at Risk Right Now
            </h3>
            <p className="text-2xl font-bold text-amber-500">
              {formatCurrency(metrics.revenueAtRisk)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {metrics.missedOpportunities > 0 && (
                <span>{metrics.missedOpportunities} opportunity{metrics.missedOpportunities > 1 ? 'ies' : 'y'} at risk</span>
              )}
            </p>
          </div>

          {metrics.criticalActions.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-muted-foreground">Most critical:</p>
              {metrics.criticalActions.map((action, idx) => (
                <div key={idx} className="flex items-center justify-between text-xs">
                  <span className="text-foreground">{action.name}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">{action.reason}</span>
                    <Badge variant="outline" className="text-[10px] border-amber-500/30 text-amber-500">
                      {formatCurrency(action.value)}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="pt-2 border-t border-amber-500/20">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Target className="w-4 h-4 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">Execution Score</span>
              </div>
              <div className="flex items-center gap-2">
                <span className={cn(
                  "text-sm font-bold",
                  metrics.executionScore >= 80 ? "text-green-500" :
                  metrics.executionScore >= 60 ? "text-amber-500" :
                  "text-red-500"
                )}>
                  {metrics.executionScore}/100
                </span>
                {metrics.executionScore < 80 && (
                  <span className="text-[10px] text-muted-foreground">
                    {metrics.executionScore < 60 ? 'Critical' : 'Needs attention'}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

